import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Stack,
  Alert,
  AlertTitle,
  InputAdornment,
} from '@mui/material';
import { StaffPayrollDto } from '../../services/payrollService';

interface EditPayrollDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  loading: boolean;
  selectedPayroll: StaffPayrollDto | null;
  editForm: Partial<StaffPayrollDto>;
  onFormChange: (form: Partial<StaffPayrollDto>) => void;
}

const EditPayrollDialog: React.FC<EditPayrollDialogProps> = ({
  open,
  onClose,
  onSave,
  loading,
  selectedPayroll,
  editForm,
  onFormChange
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Payroll</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="info">
            <AlertTitle sx={{ mb: 0.5 }}>Hourly rate is managed in Billing Rates</AlertTitle>
            The base hourly rate is sourced from <strong>Billing Rates → Staff Rates</strong> and
            applied per-segment when payroll is generated. To change pricing for this staff member,
            update their StaffBaseRate or the StaffPay segments rather than overriding the rate
            here. Editing the rate field below only affects the stored gross/net totals on this
            payroll record and will be lost on regeneration.
          </Alert>

          <TextField
            fullWidth
            label="Regular Hours"
            type="number"
            value={editForm.regularHours || 0}
            onChange={(e) => onFormChange({
              ...editForm,
              regularHours: parseFloat(e.target.value) || 0
            })}
          />
          <TextField
            fullWidth
            label="Overtime Hours"
            type="number"
            value={editForm.overtimeHours || 0}
            onChange={(e) => onFormChange({
              ...editForm,
              overtimeHours: parseFloat(e.target.value) || 0
            })}
          />
          <TextField
            fullWidth
            label="Base Hourly Rate (from Billing Rates)"
            type="number"
            value={editForm.hourlyRate || 0}
            onChange={(e) => onFormChange({
              ...editForm,
              hourlyRate: parseFloat(e.target.value) || 0
            })}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            helperText="Sourced from Billing Rates → Staff Rates. Changing this here does NOT change the segment percentages — manage those under Billing Rates."
          />
          <TextField
            fullWidth
            label="Other Deductions"
            type="number"
            value={editForm.otherDeductions || 0}
            onChange={(e) => onFormChange({
              ...editForm,
              otherDeductions: parseFloat(e.target.value) || 0
            })}
          />
          <TextField
            fullWidth
            label="Staff Approval Notes"
            multiline
            rows={3}
            value={editForm.staffApprovalNotes || ''}
            onChange={(e) => onFormChange({
              ...editForm,
              staffApprovalNotes: e.target.value
            })}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={onSave} 
          variant="contained"
          disabled={loading || !selectedPayroll}
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditPayrollDialog;
